import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpParams, HttpInterceptor, HttpHandler, HttpEvent, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { APINotFoundException, APIUnauthorizedException, ErrorMessage, APIServerError } from '../models/errors';

export abstract class ApiServiceBase
{
  abstract makeAPIGetCall<T> 
  (
      uriBase: string, 
      apiRoute: string,  
      params?: {[param: string]: string | string[]},
      headers?: {[param: string]: string | string []}
  ) : Observable<T>

  abstract makeAPIPutCall<T> 
  (
      uriBase: string, 
      apiRoute: string,  
      bodyObject: any,
      params?: {[param: string]: string | string[]},
      headers?: {[param: string]: string | string []}
  ) : Observable<T> 

  abstract makeAPIPostCall<T> 
  (
      uriBase: string, 
      apiRoute: string,  
      bodyObject: any,
      params?: {[param: string]: string | string[]},
      headers?: {[param: string]: string | string []}
  ) : Observable<T>

  abstract makeAPIPatchCall<T> 
  (
      uriBase: string, 
      apiRoute: string,  
      bodyObject: any,
      params?: {[param: string]: string | string[]},
      headers?: {[param: string]: string | string []}
  ) : Observable<T>

  abstract makeAPIDeleteCall<T> 
  (
      uriBase: string, 
      apiRoute: string,  
      params?: {[param: string]: string | string[]},
      headers?: {[param: string]: string | string []}
  ) : Observable<T>

  abstract downloadFileFromGet
  (
    uriBase: string, 
    apiRoute: string,  
    params?: {[param: string]: string | string[]},
    headers?: {[param: string]: string | string []}    
  ): Observable<ArrayBuffer>

  abstract getFileBlob
  (
    uriBase: string, 
    apiRoute: string,  
    params?: {[param: string]: string | string[]},
    headers?: {[param: string]: string | string []}    
  ): Observable<Blob>

  abstract downloadFileFromPost
  (
    uriBase: string, 
    apiRoute: string,  
    bodyObject: any,
    params?: {[param: string]: string | string[]},
    headers?: {[param: string]: string | string []}    
  ): Observable<ArrayBuffer>

}


@Injectable({
  providedIn: 'root'
})
export class ApiService extends ApiServiceBase
{

  //  = inject(HttpClient);
  //We'll use an injected Login Service for all of our token acquisition and whatnot
  constructor(private client: HttpClient) 
  { 
    super();
  }

  //This method will wrap the HttpClient calls in a way that allow for us to get model objects back from api endpoitns
  //without having to write the same code over and over again everywhere. Especially since we're doing pretty much the same
  //thing over and over again.

  //This call will:
  //Issue a Bearer Token
  //Expect a json response in the body
  //Deserialize the json into a model interface (<T>)
  
  //uriBase - The base uri for the api
  //apiRoute - the route to the specific api method
  //params - any url parameters for the get verb - optional
  //headers - any headers that we want to supply - optional

  //We return a promise of whatever type we need 
  override makeAPIGetCall<T> 
  (
      uriBase: string, 
      apiRoute: string,  
      params?: {[param: string]: string | string[]},
      headers?: {[param: string]: string | string []}
  ) : Observable<T>
  {
    

    //Start with empty headers, or whatever was passed in
      var h = headers == null ? new HttpHeaders() : new HttpHeaders(headers);
      var p = params == null ? new HttpParams() : params;

      //Get an observable of the specified type
      return this.client.get<T>(uriBase + apiRoute,
      {
        headers: h,
        observe: 'body',
        responseType: 'json',
        params: p
      }).pipe
      (
        catchError(error =>
        {
          if (error.status == "404") return throwError(() => new APINotFoundException());
          else if (error.status == "401") return throwError(() => new APIUnauthorizedException());
          else if (error.status == "500") return throwError(() =>  new APIServerError());
          else return throwError(() => error.error as ErrorMessage);
        })
      )


  }

  override makeAPIPutCall<T> 
  (
      uriBase: string, 
      apiRoute: string,  
      bodyObject: any,
      params?: {[param: string]: string | string[]},
      headers?: {[param: string]: string | string []}
  ) : Observable<T>
  {
    
      //Start with empty headers, or whatever was passed in
      var h = headers == null ? new HttpHeaders() : new HttpHeaders(headers);
      var p = params == null ? new HttpParams() : params;

      //Get an observable of the specified type
      return this.client.put<T>(uriBase + apiRoute, bodyObject,
      {
        headers: h,
        observe: 'body',
        responseType: 'json',
        params: p
      }).pipe
      (
        catchError(error =>
        {
          if (error.status == "404") return throwError(() => new APINotFoundException());
          else if (error.status == "401") return throwError(() => new APIUnauthorizedException());
          else if (error.status == "500") return throwError(() =>  new APIServerError());
          else return throwError(() => error.error as ErrorMessage);
        })
      )

  }

  override makeAPIPostCall<T> 
  (
      uriBase: string, 
      apiRoute: string,  
      bodyObject: any,
      params?: {[param: string]: string | string[]},
      headers?: {[param: string]: string | string []}
  ) : Observable<T>
  {
    
      //Start with empty headers, or whatever was passed in
      var h = headers == null ? new HttpHeaders() : new HttpHeaders(headers);
      var p = params == null ? new HttpParams() : params;

      //Get an observable of the specified type
      return this.client.post<T>(uriBase + apiRoute, bodyObject,
      {
        headers: h,
        observe: 'body',
        responseType: 'json',
        params: p
      }).pipe
      (
        catchError(error =>
        {
          if (error.status == "404") return throwError(() => new APINotFoundException());
          else if (error.status == "401") return throwError(() => new APIUnauthorizedException());
          else if (error.status == "500") return throwError(() =>  new APIServerError());
          else return throwError(() => error.error as ErrorMessage);
        })
      )

  //return null;

  }

  override makeAPIPatchCall<T> 
  (
      uriBase: string, 
      apiRoute: string,  
      bodyObject: any,
      params?: {[param: string]: string | string[]},
      headers?: {[param: string]: string | string []}
  ) : Observable<T>
  {
    

    //We have to wait for our token here...
  
      //Start with empty headers, or whatever was passed in
      var h = headers == null ? new HttpHeaders() : new HttpHeaders(headers);
      var p = params == null ? new HttpParams() : params;
      //Add our bearer token for auth (assuming we got one)

      //Get an observable of the specified type
      return this.client.patch<T>(uriBase + apiRoute, bodyObject,
      {
        headers: h,
        observe: 'body',
        responseType: 'json',
        params: p
      }).pipe
      (
        catchError(error =>
        {
          if (error.status == "404") return throwError(() => new APINotFoundException());
          else if (error.status == "401") return throwError(() => new APIUnauthorizedException());
          else if (error.status == "500") return throwError(() =>  new APIServerError());
          else return throwError(() => error.error as ErrorMessage);
        })
      )


  }

  override makeAPIDeleteCall<T> 
  (
      uriBase: string, 
      apiRoute: string,  
      params?: {[param: string]: string | string[]},
      headers?: {[param: string]: string | string []}
  ) : Observable<T>
  {
    

    //Start with empty headers, or whatever was passed in
      var h = headers == null ? new HttpHeaders() : new HttpHeaders(headers);
      var p = params == null ? new HttpParams() : params;


      //Get an observable of the specified type
      return this.client.delete<T>(uriBase + apiRoute,
      {
        headers: h,
        observe: 'body',
        responseType: 'json',
        params: p
      }).pipe
      (
        catchError(error =>
        {
          if (error.status == "404") return throwError(() => new APINotFoundException());
          else if (error.status == "401") return throwError(() => new APIUnauthorizedException());
          else if (error.status == "500") return throwError(() =>  new APIServerError());
          else return throwError(() => error.error as ErrorMessage);
        })
      )


  }

  override downloadFileFromGet
  (
    uriBase: string, 
    apiRoute: string,  
    params?: {[param: string]: string | string[]},
    headers?: {[param: string]: string | string []}    
  ): Observable<ArrayBuffer>
  {
      const options = { responseType: 'blob' };

      var h = headers == null ? new HttpHeaders() : new HttpHeaders(headers);
      var p = params == null ? new HttpParams() : params;

      return this.client.get<ArrayBuffer>(uriBase + apiRoute,
        {
          responseType:'arraybuffer' as 'json', 
          headers: h,
          params: p,
        });
  }

  override getFileBlob
  (
    uriBase: string, 
    apiRoute: string,  
    params?: {[param: string]: string | string[]},
    headers?: {[param: string]: string | string []}    
  ): Observable<Blob>
  {
      const options = { responseType: 'blob' };

      var h = headers == null ? new HttpHeaders() : new HttpHeaders(headers);
      var p = params == null ? new HttpParams() : params;

      return this.client.get<Blob>(uriBase + apiRoute,
        {
          responseType:'blob' as 'json', 
          headers: h,
          params: p,
        });
  }

  override downloadFileFromPost
  (
    uriBase: string, 
    apiRoute: string,  
    bodyObject: any,
    params?: {[param: string]: string | string[]},
    headers?: {[param: string]: string | string []}    
  ): Observable<ArrayBuffer>
  {

      var h = headers == null ? new HttpHeaders() : new HttpHeaders(headers);
      var p = params == null ? new HttpParams() : params;

      return this.client.post<ArrayBuffer>(uriBase + apiRoute, bodyObject,
        {
          responseType:'arraybuffer' as 'json', 
          headers: h,
          params: p,
        })
  }

}

export class APIException implements ErrorMessage
{

    constructor (public error: string)
    {
      this.message = error;
      this.stackTrace = "";
    }

  message: string;
  stackTrace: string;
}



export class AngularDateHttpInterceptor implements HttpInterceptor {
  // Migrated from AngularJS https://raw.githubusercontent.com/Ins87/angular-date-interceptor/master/src/angular-date-interceptor.js
  iso8601 = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/;

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      return next.handle(req).pipe(
          tap((event: HttpEvent<any>) => {
              if (event instanceof HttpResponse) {
                  const body = event.body;
                  this.convertToDate(body);
              }
          }, (err: any) => {
              if (err instanceof HttpErrorResponse) {
                  if (err.status === 401) {
                  }
              }
          }),
      );
  }

  convertToDate(body: any) {
      if (body === null || body === undefined) {
          return body;
      }

      if (typeof body !== 'object') {
          return body;
      }

      for (const key of Object.keys(body)) {
          const value = body[key];
          if (this.isIso8601(value)) {
              body[key] = new Date(value);
          } else if (typeof value === 'object') {
              this.convertToDate(value);
          }
      }
  }

  isIso8601(value: any) {
      if (value === null || value === undefined) {
          return false;
      }

      return this.iso8601.test(value);
  }
}
